<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
                >Search Form
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="loadReport">
                  <v-row class="px-2 py-2">
                    <v-col lg="4" md="4" cols="12">
                      <v-autocomplete
                        v-model="filters.lc_id"
                        :items="lcList"
                        :item-title="item => `${item.lc_no}${item.code ? ' - ' + item.code : ''}`"
                        item-value="id"
                        label="Filter by LC (Optional)"
                        density="compact"
                        prepend-inner-icon="mdi-file-document-outline"
                        variant="outlined"
                        bg-color="grey-lighten-4"
                        clearable
                        hide-details
                      ></v-autocomplete>
                    </v-col>

                    <v-col lg="2" md="2" cols="12">
                      <v-btn
                        class="bg-brand w-15"
                        type="submit"
                        :loading="loading"
                        prepend-icon="mdi-magnify"
                        >Search</v-btn
                      >
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>

    <v-container fluid style="margin-top: -20px;">
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel1">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                LC Shipment Report
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row class="py-2">
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search"
                      single-line
                      hide-details
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      class="pl-2"
                      clearable
                    ></v-text-field>
                  </v-col>

                  <v-col md="5" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group
                        v-if="reportData.length > 0"
                        variant="outlined"
                        density="compact"
                        size="x-small"
                        divided
                      >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="success"
                          @click="exportToCSV"
                          >CSV</v-btn
                        >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="primary"
                          @click="exportToExcel"
                          >EXCEL</v-btn
                        >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="red"
                          @click="exportToPDF"
                          >PDF</v-btn
                        >
                      </v-btn-group>
                    </div>
                  </v-col>

                  <v-col md="3" cols="12">
                    <div class="text-right">
                      <v-chip
                        v-if="reportData.length > 0"
                        color="primary"
                        variant="tonal"
                        size="small"
                      >
                        Total: {{ reportData.length }}
                      </v-chip>
                    </div>
                  </v-col>
                </v-row>

                <v-data-table
                  :headers="headers"
                  :items="reportData"
                  :search="search"
                  :loading="loading"
                  item-value="id"
                  class="elevation-1"
                  density="compact"
                >
                  <template v-slot:item.index="{ index }">
                    {{ index + 1 }}
                  </template>

                  <template v-slot:item.shipment_no="{ item }">
                    {{ item.shipment_no }}
                  </template>

                  <template v-slot:item.code="{ item }">
                    {{ item.code }}
                  </template>

                  <template v-slot:item.shipment_date="{ item }">
                    {{ formatDate(item.shipment_date) }}
                  </template>

                  <template v-slot:item.lc="{ item }">
                    {{ item.lc_master?.lc_no || "N/A" }}
                  </template>

                  <template v-slot:item.supplier="{ item }">
                    {{ item.lc_master?.supplier?.supplier_name || "N/A" }}
                  </template>

                  <template v-slot:item.etd="{ item }">
                    {{ item.etd ? formatDate(item.etd) : "-" }}
                  </template>

                  <template v-slot:item.eta="{ item }">
                    {{ item.eta ? formatDate(item.eta) : "-" }}
                  </template>

                  <template v-slot:item.shipment_value="{ item }">
                    {{ $numberFormat(item.shipment_value) }}
                  </template>

                  <template v-slot:item.shipment_status="{ item }">
                    <v-chip
                      :color="getShipmentStatusColor(item.shipment_status)"
                      size="small"
                      variant="tonal"
                    >
                      {{ item.shipment_status || "Draft" }}
                    </v-chip>
                  </template>

                  <template v-slot:item.document_status="{ item }">
                    <v-chip
                      :color="getDocumentStatusColor(item.document_status)"
                      size="small"
                      variant="tonal"
                    >
                      {{ item.document_status || "PENDING" }}
                    </v-chip>
                  </template>

                  <template v-slot:item.approval_status="{ item }">
                    <v-chip
                      :color="getApprovalStatusColor(item.approval_status)"
                      size="small"
                      variant="tonal"
                    >
                      {{ item.approval_status || "Pending" }}
                    </v-chip>
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <v-btn
                      icon="mdi-eye"
                      size="small"
                      variant="text"
                      color="info"
                      @click="viewItem(item.id)"
                    ></v-btn>
                  </template>

                  <template v-slot:no-data>
                    <div class="text-center pa-4">
                      <v-icon size="48" color="grey">mdi-database-search</v-icon>
                      <p class="text-grey mt-2">No shipment data found</p>
                      <p class="text-caption text-grey">Click Search to load report data</p>
                    </div>
                  </template>
                </v-data-table>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import formatDateView from "@/plugins/dateFormatView";
import { ref, computed, onMounted, getCurrentInstance } from "vue";
import { useRouter } from "vue-router";
import { toast } from "vue3-toastify";
import { useStore } from "vuex";
import Axios from "axios";
import { getLcImportShipmentReport } from "@/store/api";

export default {
  name: "LcImportShipmentReport",
  setup() {
    const store = useStore();
    const router = useRouter();
    const instance = getCurrentInstance();

    const panel = ref([0]);
    const panel1 = ref([0]);
    const loading = ref(false);
    const search = ref("");
    const reportData = ref([]);
    const filters = ref({
      lc_id: null,
    });

    const headers = [
      { title: "#SL", align: "center", key: "index", width: "5%", sortable: false },
      { title: "Shipment No", key: "shipment_no", align: "left", sortable: true },
      { title: "Shipment Code", key: "code", align: "left", sortable: true },
      { title: "Shipment Date", key: "shipment_date", align: "left", sortable: true },
      { title: "LC No", key: "lc", align: "left", sortable: false },
      { title: "Supplier", key: "supplier", align: "left", sortable: false },
      { title: "ETD", key: "etd", align: "left", sortable: true },
      { title: "ETA", key: "eta", align: "left", sortable: true },
      { title: "Shipment Value", key: "shipment_value", align: "right", sortable: true },
      { title: "Shipment Status", align: "center", key: "shipment_status", sortable: true },
      { title: "Document Status", align: "center", key: "document_status", sortable: true },
      { title: "Approval Status", align: "center", key: "approval_status", sortable: true },
      { title: "Actions", align: "center", key: "actions", sortable: false, width: "10%" },
    ];

    const lcList = computed(() => {
      return store.getters.getLcImportLcMasterList || [];
    });

    const getCurrentCompany = computed(() => {
      return store.getters.getCurrentCompany || {};
    });

    const getFileServerUrl = computed(() => {
      return store.getters.getFileServerUrl || "";
    });

    const loadReport = async () => {
      try {
        loading.value = true;
        const params = {};
        if (filters.value.lc_id) {
          params.lc_id = filters.value.lc_id;
        }

        const response = await Axios.get(getLcImportShipmentReport, { params });
        reportData.value = response.data || [];
        
        if (reportData.value.length === 0) {
          toast.info("No shipment data found for the selected criteria");
        } else {
          toast.success(`Loaded ${reportData.value.length} shipment record(s)`);
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load shipment report");
        reportData.value = [];
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (date) => {
      if (!date) return "";
      return formatDateView(date);
    };

    const getShipmentStatusColor = (status) => {
      const colors = {
        Draft: "grey",
        Shipped: "info",
        Arrived: "primary",
        Cleared: "success",
        Closed: "default",
      };
      return colors[status] || "grey";
    };

    const getDocumentStatusColor = (status) => {
      const colors = {
        PENDING: "warning",
        RECEIVED: "info",
        MISMATCH: "error",
        REJECTED: "error",
      };
      return colors[status] || "warning";
    };

    const getApprovalStatusColor = (status) => {
      const colors = {
        Pending: "warning",
        Approved: "success",
        Cancelled: "error",
      };
      return colors[status] || "warning";
    };

    const viewItem = (id) => {
      router.push({ path: `/lc/import-lc-shipment-view/${id}` });
    };

    const exportToCSV = () => {
      const items = reportData.value || [];

      if (!items.length) {
        toast.warning("No data to export");
        return;
      }

      const data = [[
        "#SL",
        "Shipment No",
        "Shipment Code",
        "Shipment Date",
        "LC No",
        "Supplier",
        "ETD",
        "ETA",
        "Shipment Value",
        "Shipment Status",
        "Document Status",
        "Approval Status",
      ]];

      items.forEach((item, index) => {
        data.push([
          index + 1,
          item.shipment_no || "",
          item.code || "",
          formatDate(item.shipment_date),
          item.lc_master?.lc_no || "N/A",
          item.lc_master?.supplier?.supplier_name || "N/A",
          item.etd ? formatDate(item.etd) : "-",
          item.eta ? formatDate(item.eta) : "-",
          item.shipment_value ?? 0,
          item.shipment_status || "Draft",
          item.document_status || "PENDING",
          item.approval_status || "Pending",
        ]);
      });

      const proxy = instance?.proxy;
      if (proxy?.$exportTableToCSV && proxy?.$fileDate) {
        proxy.$exportTableToCSV(
          data,
          "lc_shipment_report_" + proxy.$fileDate()
        );
      } else {
        toast.error("CSV export is not configured");
      }
    };

    const exportToExcel = () => {
      const items = reportData.value || [];

      if (!items.length) {
        toast.warning("No data to export");
        return;
      }

      const data = [[
        "#SL",
        "Shipment No",
        "Shipment Code",
        "Shipment Date",
        "LC No",
        "Supplier",
        "ETD",
        "ETA",
        "Shipment Value",
        "Shipment Status",
        "Document Status",
        "Approval Status",
      ]];

      items.forEach((item, index) => {
        data.push([
          index + 1,
          item.shipment_no || "",
          item.code || "",
          formatDate(item.shipment_date),
          item.lc_master?.lc_no || "N/A",
          item.lc_master?.supplier?.supplier_name || "N/A",
          item.etd ? formatDate(item.etd) : "-",
          item.eta ? formatDate(item.eta) : "-",
          item.shipment_value ?? 0,
          item.shipment_status || "Draft",
          item.document_status || "PENDING",
          item.approval_status || "Pending",
        ]);
      });

      const proxy = instance?.proxy;
      if (proxy?.$exportTableToExcel && proxy?.$fileDate) {
        proxy.$exportTableToExcel(
          data,
          "lc_shipment_report_" + proxy.$fileDate()
        );
      } else {
        toast.error("Excel export is not configured");
      }
    };

    const exportToPDF = async () => {
      const items = reportData.value || [];

      if (!items.length) {
        toast.warning("No data to export");
        return;
      }

      const company = getCurrentCompany.value;
      const fileServerUrl = getFileServerUrl.value;

      let printWindow = window.open(
        "",
        "PRINT",
        `width=${screen.width}, height=${screen.height}, left=0, top=0`
      );

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <meta http-equiv="X-UA-Compatible" content="ie=edge">
            <title>LC Shipment Report</title>
            <link rel="preconnect" href="https://fonts.googleapis.com">
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
            <link href="https://fonts.googleapis.com/css2?family=Arimo:ital,wght@0,400..700;1,400..700&display=swap" rel="stylesheet">
            <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.8/dist/css/bootstrap.min.css" rel="stylesheet">
            <link href="https://cdn.jsdelivr.net/npm/@mdi/font@7.4.47/css/materialdesignicons.min.css" rel="stylesheet">
            <style>
              body { font-family: 'Arimo', sans-serif; margin: 0px; }
              .invoice-header { display:flex; justify-content:space-between; align-items:center; border-bottom:2px solid #eee; padding-bottom:10px; }
              .invoice-header img { max-height: 70px; max-width: 150px; }
              .table th, .table td { border: 1px solid #000; padding: 8px; vertical-align: middle; font-size: 12px; }
              .table th { background-color: #f0f0f0; color: #333; }
              .text-end { text-align: right; }
              .text-center { text-align: center; }
              h4 { font-size: 20px; color: #333; }
              p { font-size: 14px; color: #555; margin: 2px 0; }
              strong { font-weight: bold; color: #333; }
              @page { margin: 0.5in; size: letter; }
              .logo-container { display:flex; align-items:center; }
              .logo-container img { margin-left: 10px; }
              .table { width: 100%; border-collapse: collapse; table-layout: auto; }
              thead { display: table-header-group; }
              .totals-row { page-break-inside: avoid; }
              .totals-row td { font-weight: bold; background-color: #f5f5f5 !important; }
            </style>
          </head>
          <body>
            <div class="container-fluid position-relative">
              <div class="invoice-header">
                <div>
                  <h4 class="text-uppercase">${company?.name || "Company Name"}</h4>
                  <p style="width:65%">${company?.address || ""}</p>
                  <p><i class="mdi mdi-phone-outline"></i> ${company?.mobile || ""}</p>
                  <p><i class="mdi mdi-email-outline"></i> ${company?.email || ""}</p>
                  ${company?.website ? `<p><i class="mdi mdi-web"></i> ${company.website}</p>` : ""}
                </div>
                <div class="logo-container">
                  ${
                    company?.logo_thumb
                      ? `<img src="${fileServerUrl}${company.logo_thumb}" alt="Logo" class="img-fluid">`
                      : ""
                  }
                </div>
              </div>
              <h4 class="text-center my-4"><strong>LC SHIPMENT REPORT</strong></h4>
              <div class="row mb-3">
                <div class="col-sm-8">
                  ${
                    filters.value.lc_id
                      ? (() => {
                          const selectedLc = lcList.value.find(
                            (lc) => lc.id === filters.value.lc_id
                          );
                          return selectedLc
                            ? `<p><strong>LC:</strong> ${selectedLc.lc_no || ""}${selectedLc.code ? " - " + selectedLc.code : ""}</p>`
                            : "";
                        })()
                      : ""
                  }
                </div>
                <div class="col-sm-4 text-end">
                  <p><strong>Total Records:</strong> ${items.length}</p>
                </div>
              </div>
              <table class="table mt-4">
                <thead>
                  <tr>
                    <th>SN</th>
                    <th>Shipment No</th>
                    <th>Shipment Code</th>
                    <th>Shipment Date</th>
                    <th>LC No</th>
                    <th>Supplier</th>
                    <th>ETD</th>
                    <th>ETA</th>
                    <th class="text-end">Shipment Value</th>
                    <th class="text-center">Shipment Status</th>
                    <th class="text-center">Document Status</th>
                    <th class="text-center">Approval Status</th>
                  </tr>
                </thead>
                <tbody>
                  ${items
                    .map((item, index) => {
                      return `
                        <tr>
                          <td>${index + 1}</td>
                          <td>${item.shipment_no || ""}</td>
                          <td>${item.code || ""}</td>
                          <td>${formatDateView(item.shipment_date)}</td>
                          <td>${item.lc_master?.lc_no || "N/A"}</td>
                          <td>${item.lc_master?.supplier?.supplier_name || "N/A"}</td>
                          <td>${item.etd ? formatDateView(item.etd) : "-"}</td>
                          <td>${item.eta ? formatDateView(item.eta) : "-"}</td>
                          <td class="text-end">${(item.shipment_value ?? 0).toLocaleString()}</td>
                          <td class="text-center">${item.shipment_status || "Draft"}</td>
                          <td class="text-center">${item.document_status || "PENDING"}</td>
                          <td class="text-center">${item.approval_status || "Pending"}</td>
                        </tr>
                      `;
                    })
                    .join("")}
                </tbody>
              </table>
            </div>
          </body>
        </html>
      `);

      printWindow.moveTo(0, 0);
      printWindow.focus();
      await new Promise((resolve) => setTimeout(resolve, 500));
      printWindow.print();
      printWindow.close();
    };

    onMounted(async () => {
      // Load LC list for filter dropdown
      if (store.getters.getLcImportLcMasterList.length === 0) {
        try {
          await store.dispatch("getLcImportLcMasterList");
        } catch (error) {
          console.error("Failed to load LC list:", error);
        }
      }
    });

    return {
      panel,
      panel1,
      loading,
      search,
      reportData,
      filters,
      headers,
      lcList,
      loadReport,
      formatDate,
      getShipmentStatusColor,
      getDocumentStatusColor,
      getApprovalStatusColor,
      viewItem,
      exportToCSV,
      exportToExcel,
      exportToPDF,
    };
  },
};
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}
</style>

